import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { getRetentionOffer, claimRetentionOffer } from "@/lib/retention.functions";
import { Button } from "@/components/ui/button";
import { Heart, Loader2, CheckCircle2 } from "lucide-react";

export const Route = createFileRoute("/retention/$token")({
  component: RetentionAcceptPage,
  head: () => ({
    meta: [
      { title: "A month on us — Recovery Trauma™" },
      {
        name: "description",
        content:
          "Claim one free month of your Recovery Trauma™ membership. No payment taken, cancel again anytime.",
      },
      { property: "og:title", content: "A month on us — Recovery Trauma™" },
      {
        property: "og:description",
        content:
          "Claim one free month of your Recovery Trauma™ membership. No payment taken, cancel again anytime.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
});

function RetentionAcceptPage() {
  const { token } = Route.useParams();
  const lookup = useServerFn(getRetentionOffer);
  const claim = useServerFn(claimRetentionOffer);

  const [loading, setLoading] = useState(true);
  const [state, setState] = useState<{
    found: boolean;
    status?: string;
    expired?: boolean;
    email_masked?: string;
  } | null>(null);
  const [claiming, setClaiming] = useState(false);
  const [result, setResult] = useState<{ ok: boolean; message: string } | null>(null);

  useEffect(() => {
    let active = true;
    lookup({ data: { token } })
      .then((r) => {
        if (active) setState(r);
      })
      .catch(() => {
        if (active) setState({ found: false });
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [lookup, token]);

  async function onClaim() {
    setClaiming(true);
    try {
      const r = await claim({ data: { token } });
      setResult({ ok: r.ok, message: r.message });
    } catch {
      setResult({
        ok: false,
        message: "Something went wrong. Please reply to your email and we'll help you personally.",
      });
    } finally {
      setClaiming(false);
    }
  }

  const alreadyClaimed = state?.status === "accepted" || state?.status === "completed";
  const unavailable =
    !loading && (!state?.found || state?.expired || state?.status === "declined");

  return (
    <main className="min-h-[70vh] bg-background px-4 py-16">
      <div className="mx-auto max-w-xl rounded-3xl border border-border bg-card p-8 shadow-sm sm:p-10">
        <div className="mb-6 flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/10 text-primary">
          <Heart className="h-6 w-6" aria-hidden />
        </div>

        {loading ? (
          <p className="flex items-center gap-2 text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> Checking your offer…
          </p>
        ) : unavailable ? (
          <>
            <h1 className="mb-3 text-2xl font-semibold text-foreground">
              This offer isn't available
            </h1>
            <p className="mb-6 text-muted-foreground">
              This link may have expired or already been used. If you'd still like to come back,
              your place at Recovery Trauma™ is always open.
            </p>
            <Button asChild>
              <Link to="/membership">Join Recovery Trauma™</Link>
            </Button>
          </>
        ) : result?.ok || alreadyClaimed ? (
          <>
            <h1 className="mb-3 flex items-center gap-2 text-2xl font-semibold text-foreground">
              <CheckCircle2 className="h-6 w-6 text-primary" aria-hidden /> Your free month is on
            </h1>
            <p className="mb-6 text-muted-foreground">
              {result?.message ??
                "Your membership stays open and nothing will be charged for the next month."}{" "}
              You can cancel again at any time — nothing is locked in.
            </p>
            <Button asChild>
              <Link to="/dashboard">Go to my dashboard</Link>
            </Button>
          </>
        ) : (
          <>
            <h1 className="mb-3 text-2xl font-semibold text-foreground">Would one more month help?</h1>
            <p className="mb-4 text-muted-foreground">
              You recently cancelled your Recovery Trauma™ membership
              {state?.email_masked ? ` (${state.email_masked})` : ""}. If cost or timing played a
              part, we'd like to give you <strong className="text-foreground">one month on us</strong>.
            </p>
            <ul className="mb-6 space-y-2 text-sm text-muted-foreground">
              <li>• No payment is taken for your next month.</li>
              <li>• Your access continues without interruption.</li>
              <li>• You can cancel again at any time.</li>
            </ul>
            {result && !result.ok ? (
              <p className="mb-4 rounded-xl bg-destructive/10 p-3 text-sm text-destructive">
                {result.message}
              </p>
            ) : null}
            <div className="flex flex-col gap-3 sm:flex-row">
              <Button onClick={onClaim} disabled={claiming} size="lg">
                {claiming ? (
                  <>
                    <Loader2 className="me-2 h-4 w-4 animate-spin" aria-hidden /> Applying…
                  </>
                ) : (
                  "Claim my free month"
                )}
              </Button>
              <Button asChild variant="ghost" size="lg">
                <Link to="/">No thanks</Link>
              </Button>
            </div>
          </>
        )}
      </div>
    </main>
  );
}
